import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Box,
  Button,
  CircularProgress,
  Tab,
  Typography,
} from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { CHILL_PORTIONS_NAME } from '../../../../constants/graphs';
import useChartConfig from '../../../../hooks/useChartConfig';
import { chillPortionsConfig } from '../../../../requests/api/apiClient';
import ConversationsTab from '../../ConversationTab';
import useStyles from '../../styles';
import DayMonthBaseForm, { ConfigState } from '../../utils/DayMonthBaseForm';
import FORM_CONFIGS from './constants';
import { ChillPortionsConfigType, DEFAULT_CHILL_PORTIONS_CONFIG } from './types';
import { validateConfig } from './validations';
import { ModalState, EMPTY_MODAL_STATE } from '../../chartModal';
import useIsClientOperator from '../../../../hooks/useIsClientOperator';

const transformBackendData = (
  backendConfig: Record<string, number>,
  defaultConfig: ChillPortionsConfigType,
): ChillPortionsConfigType => ({
  chillPortionsStartMonth: backendConfig.chillPortionsStartMonth
    ?? defaultConfig.chillPortionsStartMonth,
  chillPortionsStartDay: backendConfig.chillPortionsStartDay
    ?? defaultConfig.chillPortionsStartDay,
  chillPortionsEndMonth: backendConfig.chillPortionsEndMonth
    ?? defaultConfig.chillPortionsEndMonth,
  chillPortionsEndDay: backendConfig.chillPortionsEndDay
    ?? defaultConfig.chillPortionsEndDay,
});

const ChillPortionsConfigContent = ({
  sectorId,
  setModalState: setModalStateProp,
  refreshCounter = 0,
  onConfigSaved,
}: {
  sectorId: string;
  setModalState?: React.Dispatch<React.SetStateAction<ModalState>>;
  refreshCounter?: number;
  onConfigSaved?: () => void;
}) => {
  const classes = useStyles();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);

  // Por defecto vamos a Conversaciones cuando se llega desde una notificación.
  const date = queryParams.get('date');
  const initialTabValue = date ? '2' : '1';

  const [tabValue, setTabValue] = useState(initialTabValue);

  const [, setInternalModalState] = useState<ModalState>(EMPTY_MODAL_STATE);
  const setModalState = setModalStateProp ?? setInternalModalState;

  const {
    formConfig,
    loading,
    errors,
    handleConfigChange,
    handleSave,
  } = useChartConfig<ChillPortionsConfigType>({
    sectorId,
    apiService: chillPortionsConfig,
    defaultConfig: DEFAULT_CHILL_PORTIONS_CONFIG,
    configKey: 'chillPortionsConfig',
    validateConfig,
    transformBackendData,
    messages: {
      loadError: 'No se pudo cargar la configuración de porciones de frío',
      saveSuccess: 'Configuración guardada correctamente',
      saveError: 'Error al guardar la configuración',
      validationError: 'Por favor, resolver errores en el formulario',
    },
  });

  const isClientOperator = useIsClientOperator();

  useEffect(() => {
    if (isClientOperator && tabValue === '1') {
      setTabValue('2');
    }
  }, [isClientOperator, tabValue]);

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  }, []);

  const onSave = useCallback(async () => {
    const saved = await handleSave();
    // onConfigSaved refreshes chart data — only fire when the save actually succeeded.
    if (saved) {
      onConfigSaved?.();
    }
  }, [handleSave, onConfigSaved]);

  const renderForms = useCallback(() => (
    FORM_CONFIGS.map((formConfigItem) => (
      <DayMonthBaseForm
        key={formConfigItem.title}
        {...formConfigItem}
        config={formConfig as ConfigState}
        onConfigChange={handleConfigChange as (
          field: keyof ConfigState, value: string | number) => void}
        errors={errors}
      />
    ))
  ), [formConfig, errors, handleConfigChange]);

  return (
    <TabContext value={tabValue}>
      <Box sx={{ borderBottom: 0 }}>
        <TabList onChange={handleTabChange} aria-label="sector tabs">
          {!isClientOperator && (
            <Tab label="Configuración" value="1" className={classes.tabLabel} />
          )}
          <Tab label="Conversaciones" value="2" className={classes.tabLabel} />
        </TabList>
      </Box>
      {!isClientOperator && (
        <TabPanel value="1" className={classes.configTabPanel}>
          {renderForms()}
          <Typography variant="body2" color="textSecondary" mt={1}>
            El modelo dinámico de Fishman/Erez sólo usa la temperatura horaria, por
            lo que no requiere una temperatura base. El acumulado parte siempre
            desde el inicio de la temporada configurada.
          </Typography>
          <Box mt={3} display="flex" justifyContent="flex-end">
            {loading ? <CircularProgress /> : (
              <Button
                variant="contained"
                color="primary"
                onClick={onSave}
                disabled={loading || Object.keys(errors).length > 0}
              >
                Guardar
              </Button>
            )}
          </Box>
        </TabPanel>
      )}
      <TabPanel value="2" className={classes.configTabPanel}>
        <ConversationsTab
          setModalState={setModalState}
          sectorId={sectorId}
          chartName={CHILL_PORTIONS_NAME}
          refreshCounter={refreshCounter}
        />
      </TabPanel>
    </TabContext>
  );
};

export default ChillPortionsConfigContent;
